// Drawing a board (or part of it) to a canvas / SVG for PNG export, thumbnails and printing.
import type { BoardBackground, FontKey, LayerElement, PageElement, PatternKey, SepiaElement } from "../../shared/model";
import { bounds, frameBox } from "./elements";
import { type Box, boxesIntersect, unionBoxes } from "./geometry";
import { inkElementPath, PENS } from "./ink";
import { linePolyline, sketchLine, sketchShape } from "./sketch";
import { adaptInk, deskColor, FONTS, PAPERS } from "./style";
import { cardTitle } from "./markdown";

export interface RenderOptions {
  bg: BoardBackground;
  scale?: number;
  padding?: number;
  box?: Box;
  transparent?: boolean;
  grid?: boolean;
  resolveImage: (src: string) => string;
  stickerUrl: (key: string) => string;
  maxPixels?: number;
  /** the board's layers, bottom to top: hidden ones are left out, opacity and blend modes applied */
  layers?: LayerElement[];
  layerOf?: (el: SepiaElement) => string;
  /** a paged board's pages: drawn as sheets of paper on the desk, and the drawing is cut off at their edges */
  pages?: PageElement[];
}

const byZ = (a: SepiaElement, b: SepiaElement) => (a.z < b.z ? -1 : a.z > b.z ? 1 : 0);

/** Elements grouped by visible layer, bottom layer first (one group, unlayered, when no layers are given). */
function layerGroups(els: SepiaElement[], o: RenderOptions): { layer: LayerElement | null; els: SepiaElement[] }[] {
  const drawable = els.filter((e) => e.type !== "layer" && e.type !== "page");
  if (!o.layers?.length || !o.layerOf) return [{ layer: null, els: drawable.sort(byZ) }];
  const groups = new Map<string, SepiaElement[]>();
  for (const e of drawable) {
    const id = o.layerOf(e);
    if (!groups.has(id)) groups.set(id, []);
    groups.get(id)!.push(e);
  }
  return o.layers.filter((l) => l.visible && groups.has(l.id)).map((l) => ({ layer: l, els: groups.get(l.id)!.sort(byZ) }));
}

const COMPOSITE: Record<string, GlobalCompositeOperation> = {
  normal: "source-over", multiply: "multiply", screen: "screen", overlay: "overlay", darken: "darken", lighten: "lighten",
  "color-burn": "color-burn", "color-dodge": "color-dodge", "soft-light": "soft-light", difference: "difference", luminosity: "luminosity"
};

const imgCache = new Map<string, Promise<HTMLImageElement | null>>();

function loadImage(url: string): Promise<HTMLImageElement | null> {
  let p = imgCache.get(url);
  if (!p) {
    p = new Promise((res) => {
      const img = new Image();
      img.decoding = "async";
      img.onload = () => res(img);
      img.onerror = () => res(null);
      img.src = url;
    });
    imgCache.set(url, p);
  }
  return p;
}

function font(k: FontKey, size: number, weight = 400, italic = false): string {
  const f = FONTS[k];
  return `${italic ? "italic " : ""}${weight} ${size * f.scale}px ${f.family}`;
}

function wrapLines(ctx: CanvasRenderingContext2D, text: string, maxW: number): string[] {
  const out: string[] = [];
  for (const para of text.split("\n")) {
    if (!maxW || ctx.measureText(para).width <= maxW) {
      out.push(para);
      continue;
    }
    let line = "";
    for (const word of para.split(/(\s+)/)) {
      const next = line + word;
      if (ctx.measureText(next).width > maxW && line.trim()) {
        out.push(line.trimEnd());
        line = word.trimStart();
        // very long words: hard-break
        while (ctx.measureText(line).width > maxW && line.length > 1) {
          let cut = line.length - 1;
          while (cut > 1 && ctx.measureText(line.slice(0, cut)).width > maxW) cut--;
          out.push(line.slice(0, cut));
          line = line.slice(cut);
        }
      } else line = next;
    }
    out.push(line);
  }
  return out;
}

function drawTextBlock(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, w: number, h: number, f: FontKey, size: number, color: string, align: CanvasTextAlign, vcenter: boolean, weight = 400, italic = false) {
  ctx.save();
  ctx.font = font(f, size, weight, italic);
  ctx.fillStyle = color;
  ctx.textBaseline = "top";
  ctx.textAlign = align;
  const lh = size * FONTS[f].scale * FONTS[f].lineHeight;
  const lines = wrapLines(ctx, text, w);
  const total = lines.length * lh;
  let ty = vcenter ? y + (h - total) / 2 : y;
  const tx = align === "center" ? x + w / 2 : align === "right" ? x + w : x;
  for (const line of lines) {
    if (h && ty > y + h + lh) break;
    ctx.fillText(line, tx, ty + (lh - size * FONTS[f].scale) / 2);
    ty += lh;
  }
  ctx.restore();
}

function paintBackground(ctx: CanvasRenderingContext2D, box: Box, bg: BoardBackground, grid: boolean): void {
  const paper = PAPERS[bg.paper] ?? PAPERS.cream;
  ctx.fillStyle = paper.color;
  ctx.fillRect(box.x, box.y, box.w, box.h);
  if (!grid || bg.pattern === "blank") return;
  const c = bg.size;
  const x0 = Math.floor(box.x / c) * c;
  const y0 = Math.floor(box.y / c) * c;
  ctx.save();
  ctx.lineWidth = 1;
  if (bg.pattern === "dots") {
    for (let x = x0; x <= box.x + box.w; x += c) for (let y = y0; y <= box.y + box.h; y += c) {
      ctx.fillStyle = Math.round(x / c) % 5 === 0 && Math.round(y / c) % 5 === 0 ? paper.major : paper.line;
      ctx.beginPath();
      ctx.arc(x, y, 1.3, 0, Math.PI * 2);
      ctx.fill();
    }
  } else if (bg.pattern === "lines") {
    const lc = c * 1.25;
    ctx.strokeStyle = paper.major;
    for (let y = Math.floor(box.y / lc) * lc; y <= box.y + box.h; y += lc) {
      ctx.beginPath();
      ctx.moveTo(box.x, y);
      ctx.lineTo(box.x + box.w, y);
      ctx.stroke();
    }
  } else {
    for (let x = x0; x <= box.x + box.w; x += c) {
      ctx.strokeStyle = Math.round(x / c) % 5 === 0 ? paper.major : paper.line;
      ctx.beginPath();
      ctx.moveTo(x, box.y);
      ctx.lineTo(x, box.y + box.h);
      ctx.stroke();
    }
    for (let y = y0; y <= box.y + box.h; y += c) {
      ctx.strokeStyle = Math.round(y / c) % 5 === 0 ? paper.major : paper.line;
      ctx.beginPath();
      ctx.moveTo(box.x, y);
      ctx.lineTo(box.x + box.w, y);
      ctx.stroke();
    }
  }
  ctx.restore();
}

/** Where a page's ruling goes, matching the screen: grids and dots centred on the page, rules under a header band. */
function pageRuling(pg: Box, pattern: PatternKey, bg: BoardBackground): { lines: [number, number, number, number, string][]; dots: [number, number, number, string][] } {
  const paper = PAPERS[bg.paper] ?? PAPERS.cream;
  const c = bg.size;
  const lines: [number, number, number, number, string][] = [];
  const dots: [number, number, number, string][] = [];
  const x1 = pg.x + pg.w;
  const y1 = pg.y + pg.h;
  if (pattern === "lines") {
    const rule = c * 1.25;
    for (let y = pg.y + rule * 3; y <= y1; y += rule) lines.push([pg.x, y, x1, y, paper.major]);
    lines.push([pg.x + rule * 2.4, pg.y, pg.x + rule * 2.4, y1, "rgba(194, 88, 58, 0.4)"]);
  } else if (pattern === "grid" || pattern === "dots") {
    // tiles centred on the page, like CSS background-position: center
    const ox = pg.x + (pg.w - c) / 2;
    const oy = pg.y + (pg.h - c) / 2;
    const mx = pg.x + (pg.w - 5 * c) / 2;
    const my = pg.y + (pg.h - 5 * c) / 2;
    const first = (o: number, lo: number, step: number) => o - Math.ceil((o - lo) / step) * step;
    if (pattern === "grid") {
      for (let x = first(ox, pg.x, c); x <= x1; x += c) lines.push([x, pg.y, x, y1, paper.line]);
      for (let y = first(oy, pg.y, c); y <= y1; y += c) lines.push([pg.x, y, x1, y, paper.line]);
      for (let x = first(mx, pg.x, 5 * c); x <= x1; x += 5 * c) lines.push([x, pg.y, x, y1, paper.major]);
      for (let y = first(my, pg.y, 5 * c); y <= y1; y += 5 * c) lines.push([pg.x, y, x1, y, paper.major]);
    } else {
      for (let x = first(ox, pg.x, c) + c / 2; x <= x1; x += c) {
        for (let y = first(oy, pg.y, c) + c / 2; y <= y1; y += c) dots.push([x, y, 1.1, paper.line]);
      }
      for (let x = first(mx, pg.x, 5 * c) + 2.5 * c; x <= x1; x += 5 * c) {
        for (let y = first(my, pg.y, 5 * c) + 2.5 * c; y <= y1; y += 5 * c) dots.push([x, y, 1.5, paper.major]);
      }
    }
  } else if (pattern === "iso") {
    const w = c * Math.sqrt(3);
    for (let y = pg.y; y <= y1; y += c) lines.push([pg.x, y, x1, y, paper.major]);
    // the two diagonal families, long enough to cross the page
    for (let x = pg.x - pg.h * Math.sqrt(3); x <= x1; x += w) {
      lines.push([x, pg.y, x + pg.h * Math.sqrt(3), y1, paper.major]);
      lines.push([x + pg.h * Math.sqrt(3), pg.y, x, y1, paper.major]);
    }
  }
  return { lines, dots };
}

/** The desk and the pages lying on it (with their ruling unless plain paper was asked for). */
function paintPages(ctx: CanvasRenderingContext2D, box: Box, o: RenderOptions): void {
  const paper = PAPERS[o.bg.paper] ?? PAPERS.cream;
  ctx.fillStyle = deskColor(o.bg);
  ctx.fillRect(box.x, box.y, box.w, box.h);
  for (const pg of o.pages ?? []) {
    if (!boxesIntersect(pg, box)) continue;
    ctx.save();
    ctx.shadowColor = "rgba(43, 33, 26, 0.3)";
    ctx.shadowBlur = 16;
    ctx.shadowOffsetY = 5;
    ctx.fillStyle = paper.color;
    ctx.fillRect(pg.x, pg.y, pg.w, pg.h);
    ctx.restore();
    if (o.grid === false) continue;
    const r = pageRuling(pg, pg.pattern ?? o.bg.pattern, o.bg);
    ctx.save();
    ctx.beginPath();
    ctx.rect(pg.x, pg.y, pg.w, pg.h);
    ctx.clip();
    ctx.lineWidth = 1;
    for (const [ax, ay, bx, by, color] of r.lines) {
      ctx.strokeStyle = color;
      ctx.beginPath();
      ctx.moveTo(ax, ay);
      ctx.lineTo(bx, by);
      ctx.stroke();
    }
    for (const [x, y, rad, color] of r.dots) {
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(x, y, rad, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }
}

function markdownToLines(md: string): { text: string; kind: "h1" | "h2" | "p" | "li" | "task" | "done" }[] {
  const out: { text: string; kind: "h1" | "h2" | "p" | "li" | "task" | "done" }[] = [];
  for (const raw of md.split("\n")) {
    const line = raw.replace(/\*\*|__|`|~~/g, "").replace(/\[\[([^\]|]+)(\|([^\]]+))?\]\]/g, (_m, a, _b, c) => c || a).replace(/\[([^\]]+)\]\([^)]+\)/g, "$1");
    if (/^#\s/.test(line)) out.push({ text: line.replace(/^#\s*/, ""), kind: "h1" });
    else if (/^#{2,6}\s/.test(line)) out.push({ text: line.replace(/^#+\s*/, ""), kind: "h2" });
    else if (/^\s*[-*+]\s\[[xX]\]\s/.test(line)) out.push({ text: "☑ " + line.replace(/^\s*[-*+]\s\[[xX]\]\s/, ""), kind: "done" });
    else if (/^\s*[-*+]\s\[ \]\s/.test(line)) out.push({ text: "☐ " + line.replace(/^\s*[-*+]\s\[ \]\s/, ""), kind: "task" });
    else if (/^\s*([-*+]|\d+[.)])\s/.test(line)) out.push({ text: "• " + line.replace(/^\s*([-*+]|\d+[.)])\s/, ""), kind: "li" });
    else out.push({ text: line, kind: "p" });
  }
  return out;
}

async function drawElement(ctx: CanvasRenderingContext2D, el: SepiaElement, o: RenderOptions, dark: boolean): Promise<void> {
  ctx.save();
  ctx.globalAlpha = el.opacity ?? 1;
  if (el.type !== "line") {
    ctx.translate(el.x, el.y);
    if (el.rot) {
      ctx.translate(el.w / 2, el.h / 2);
      ctx.rotate(el.rot);
      ctx.translate(-el.w / 2, -el.h / 2);
    }
  }
  switch (el.type) {
    case "ink": {
      const preset = PENS[el.pen] ?? PENS.fountain;
      ctx.globalAlpha *= preset.opacity;
      if (preset.blend && !dark) ctx.globalCompositeOperation = "multiply";
      ctx.fillStyle = preset.blend ? el.color : adaptInk(el.color, dark);
      ctx.fill(new Path2D(inkElementPath(el)));
      break;
    }
    case "shape": {
      const sx = el.w / Math.max(2, Math.round(el.w * 2) / 2);
      const sy = el.h / Math.max(2, Math.round(el.h * 2) / 2);
      ctx.save();
      ctx.scale(sx, sy);
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      for (const p of sketchShape({ ...el, stroke: el.fillStyle === "solid" ? el.stroke : adaptInk(el.stroke, dark) })) {
        const path = new Path2D(p.d);
        if (p.fill && p.fill !== "none") {
          ctx.fillStyle = p.fill;
          ctx.fill(path);
        }
        if (p.stroke && p.stroke !== "none") {
          ctx.strokeStyle = p.stroke;
          ctx.lineWidth = p.strokeWidth;
          ctx.setLineDash(p.dash ? p.dash.split(" ").map(Number) : []);
          ctx.stroke(path);
        }
      }
      ctx.restore();
      if (el.text) drawTextBlock(ctx, el.text, el.w * 0.1, el.h * 0.08, el.w * 0.8, el.h * 0.84, el.font, el.fontSize, el.fillStyle === "solid" ? el.textColor : adaptInk(el.textColor, dark), el.align === "left" ? "left" : el.align === "right" ? "right" : "center", true);
      break;
    }
    case "line": {
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      for (const p of sketchLine({ ...el, stroke: adaptInk(el.stroke, dark) })) {
        const path = new Path2D(p.d);
        if (p.fill && p.fill !== "none") {
          ctx.fillStyle = p.fill;
          ctx.fill(path);
        }
        ctx.strokeStyle = p.stroke;
        ctx.lineWidth = p.strokeWidth;
        ctx.setLineDash(p.dash ? p.dash.split(" ").map(Number) : []);
        ctx.stroke(path);
      }
      if (el.label) {
        const poly = linePolyline(el);
        const m = poly.length === 2 ? { x: (poly[0].x + poly[1].x) / 2, y: (poly[0].y + poly[1].y) / 2 } : poly[Math.floor(poly.length / 2)];
        ctx.font = font(el.font, el.fontSize);
        const w = ctx.measureText(el.label).width + 16;
        ctx.fillStyle = PAPERS[o.bg.paper]?.color ?? "#f7f0e3";
        ctx.fillRect(m.x - w / 2, m.y - el.fontSize * 0.7, w, el.fontSize * 1.4);
        drawTextBlock(ctx, el.label, m.x - w / 2, m.y - el.fontSize * 0.7, w, el.fontSize * 1.4, el.font, el.fontSize, adaptInk(el.stroke, dark), "center", true);
      }
      break;
    }
    case "text":
      drawTextBlock(ctx, el.text, 0, 0, el.autoW ? 0 : el.w, 0, el.font, el.fontSize, adaptInk(el.color, dark), el.align === "center" ? "center" : el.align === "right" ? "right" : "left", false, el.bold ? 700 : 400, el.italic);
      break;
    case "note": {
      if (el.shadow !== false) {
        ctx.shadowColor = "rgba(60,40,20,0.25)";
        ctx.shadowBlur = 12;
        ctx.shadowOffsetY = 6;
      }
      ctx.fillStyle = el.paper;
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(el.w, 0);
      ctx.lineTo(el.w, el.h * 0.84);
      ctx.lineTo(el.w * 0.84, el.h);
      ctx.lineTo(0, el.h);
      ctx.closePath();
      ctx.fill();
      ctx.shadowColor = "transparent";
      ctx.fillStyle = "rgba(107,74,42,0.28)";
      ctx.beginPath();
      ctx.moveTo(el.w, el.h * 0.84);
      ctx.lineTo(el.w * 0.84, el.h * 0.84);
      ctx.lineTo(el.w * 0.84, el.h);
      ctx.closePath();
      ctx.fill();
      if (el.tape) {
        ctx.save();
        ctx.translate(el.w / 2, 0);
        ctx.rotate((-3 * Math.PI) / 180);
        ctx.globalAlpha *= 0.8;
        ctx.fillStyle = el.tape;
        ctx.fillRect(-el.w * 0.19, -13, el.w * 0.38, 26);
        ctx.restore();
      }
      drawTextBlock(ctx, el.text, el.w * 0.1, el.h * 0.11, el.w * 0.8, el.h * 0.75, el.font, el.fontSize, el.textColor, el.align === "center" ? "center" : el.align === "right" ? "right" : "left", false);
      break;
    }
    case "card": {
      if (el.shadow !== false) {
        ctx.shadowColor = "rgba(60,40,20,0.22)";
        ctx.shadowBlur = 12;
        ctx.shadowOffsetY = 5;
      }
      ctx.fillStyle = el.paper;
      ctx.fillRect(0, 0, el.w, el.h);
      ctx.shadowColor = "transparent";
      ctx.save();
      ctx.beginPath();
      ctx.rect(0, 0, el.w, el.h);
      ctx.clip();
      ctx.strokeStyle = "rgba(98,138,180,0.22)";
      ctx.lineWidth = 1;
      for (let y = 48 + 26; y < el.h; y += 26) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(el.w, y);
        ctx.stroke();
      }
      ctx.strokeStyle = el.accent;
      ctx.globalAlpha *= 0.55;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(0, 47);
      ctx.lineTo(el.w, 47);
      ctx.stroke();
      ctx.globalAlpha = el.opacity ?? 1;
      let y = 12;
      for (const line of markdownToLines(el.md)) {
        const size = line.kind === "h1" ? el.fontSize * 1.55 : line.kind === "h2" ? el.fontSize * 1.25 : el.fontSize;
        const f: FontKey = line.kind === "h1" || line.kind === "h2" ? "serif" : "sans";
        ctx.font = font(f, size, line.kind.startsWith("h") ? 600 : 400);
        const lines = wrapLines(ctx, line.text, el.w - 36);
        for (const l of lines) {
          if (y > el.h) break;
          ctx.fillStyle = line.kind === "done" ? "#8b7462" : "#2d241c";
          ctx.textBaseline = "top";
          ctx.fillText(l, 18, y);
          y += size * 1.5;
        }
        if (line.kind === "h1") y = Math.max(y, 56);
      }
      ctx.restore();
      break;
    }
    case "image": {
      const img = await loadImage(o.resolveImage(el.src));
      let ix = 0, iy = 0, iw = el.w, ih = el.h;
      if (el.frame === "polaroid") {
        ctx.shadowColor = "rgba(60,40,20,0.25)";
        ctx.shadowBlur = 12;
        ctx.shadowOffsetY = 6;
        ctx.fillStyle = "#fdfbf5";
        ctx.fillRect(0, 0, el.w, el.h);
        ctx.shadowColor = "transparent";
        ix = el.w * 0.05;
        iy = el.h * 0.05;
        iw = el.w * 0.9;
        ih = el.h * 0.77;
        if (el.caption) drawTextBlock(ctx, el.caption, el.w * 0.05, el.h * 0.84, el.w * 0.9, el.h * 0.13, "hand", Math.min(42, el.w * 0.07), "#3b2c20", "center", true);
      }
      if (img) {
        // object-fit: cover
        const k = Math.max(iw / img.naturalWidth, ih / img.naturalHeight);
        const sw = iw / k;
        const sh = ih / k;
        ctx.drawImage(img, (img.naturalWidth - sw) / 2, (img.naturalHeight - sh) / 2, sw, sh, ix, iy, iw, ih);
      }
      break;
    }
    case "frame": {
      ctx.strokeStyle = el.color;
      ctx.lineWidth = 2;
      ctx.setLineDash([8, 6]);
      ctx.strokeRect(0, 0, el.w, el.h);
      ctx.setLineDash([]);
      ctx.font = font("hand", 24, 600);
      const tw = ctx.measureText(el.title).width + 28;
      ctx.fillStyle = el.color;
      ctx.globalAlpha *= 0.55;
      ctx.fillRect(-2, -38, tw, 32);
      ctx.globalAlpha = el.opacity ?? 1;
      ctx.fillStyle = "#2b211a";
      ctx.textBaseline = "middle";
      ctx.fillText(el.title, 12, -22);
      break;
    }
    case "sticker": {
      const img = await loadImage(o.stickerUrl(el.key));
      if (img) ctx.drawImage(img, 0, 0, el.w, el.h);
      break;
    }
  }
  ctx.restore();
}

/**
 * Hand a canvas's memory back now. iPad Safari has a small budget for all canvases together and counts a canvas until it
 * is garbage collected; past the budget, new canvases silently stay blank (which made layers vanish from exports).
 */
export function releaseCanvas(c: HTMLCanvasElement | null | undefined): void {
  if (!c) return;
  c.width = 0;
  c.height = 0;
}

export function contentBox(els: SepiaElement[]): Box | null {
  return unionBoxes(els.map((e) => (e.type === "frame" ? frameBox(e) : bounds(e))));
}

async function ensureFonts(els: SepiaElement[]): Promise<void> {
  if (!document.fonts) return;
  const used = new Set<FontKey>(["hand", "sans", "serif"]);
  for (const e of els) if ("font" in e) used.add((e as { font: FontKey }).font);
  await Promise.all([...used].map((f) => document.fonts.load(`24px ${FONTS[f].family.split(",")[0]}`).catch(() => undefined)));
}

export async function renderToCanvas(els: SepiaElement[], o: RenderOptions): Promise<HTMLCanvasElement> {
  const pad = o.padding ?? 40;
  const content = o.box ?? contentBox(els) ?? { x: 0, y: 0, w: 800, h: 600 };
  const box = { x: content.x - pad, y: content.y - pad, w: content.w + pad * 2, h: content.h + pad * 2 };
  let scale = o.scale ?? 2;
  const maxPixels = o.maxPixels ?? 16_000_000;
  if (box.w * box.h * scale * scale > maxPixels) scale = Math.sqrt(maxPixels / (box.w * box.h));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(box.w * scale));
  canvas.height = Math.max(1, Math.round(box.h * scale));
  const ctx = canvas.getContext("2d")!;
  ctx.scale(scale, scale);
  ctx.translate(-box.x, -box.y);
  await ensureFonts(els);
  const dark = !!PAPERS[o.bg.paper]?.dark;
  const pages = o.pages?.length ? o.pages : null;
  if (!o.transparent) {
    if (pages) paintPages(ctx, box, o);
    else paintBackground(ctx, box, o.bg, o.grid ?? true);
  }
  if (pages) {
    // like on paper: nothing shows past the edge of a page
    ctx.save();
    ctx.beginPath();
    for (const pg of pages) ctx.rect(pg.x, pg.y, pg.w, pg.h);
    ctx.clip();
  }
  // one scratch sheet, reused for every layer that has its own opacity or blend mode (and freed at the end)
  let sheet: HTMLCanvasElement | null = null;
  try {
    for (const g of layerGroups(els, o)) {
      const opacity = g.layer?.opacity ?? 1;
      const blend = g.layer?.blend ?? "normal";
      if (opacity === 1 && blend === "normal") {
        for (const el of g.els) await drawElement(ctx, el, o, dark);
        continue;
      }
      // draw the layer on its own sheet, then lay it down with its opacity and blend mode
      if (!sheet) {
        sheet = document.createElement("canvas");
        sheet.width = canvas.width;
        sheet.height = canvas.height;
      }
      const sctx = sheet.getContext("2d")!;
      sctx.setTransform(1, 0, 0, 1, 0, 0);
      sctx.globalAlpha = 1;
      sctx.globalCompositeOperation = "source-over";
      sctx.clearRect(0, 0, sheet.width, sheet.height);
      sctx.setTransform(scale, 0, 0, scale, -box.x * scale, -box.y * scale);
      for (const el of g.els) await drawElement(sctx, el, o, dark);
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.globalAlpha = opacity;
      ctx.globalCompositeOperation = COMPOSITE[blend] ?? "source-over";
      ctx.drawImage(sheet, 0, 0);
      ctx.restore();
    }
  } finally {
    releaseCanvas(sheet);
  }
  if (pages) ctx.restore();
  return canvas;
}

export function canvasToBlob(canvas: HTMLCanvasElement, type = "image/png", quality = 0.92): Promise<Blob> {
  return new Promise((res, rej) => canvas.toBlob((b) => (b ? res(b) : rej(new Error("Export failed"))), type, quality));
}

/** Small JPEG preview for the library. */
export async function makeThumbnail(els: SepiaElement[], bg: BoardBackground, resolveImage: (s: string) => string, stickerUrl: (k: string) => string, layers?: { layers: LayerElement[]; layerOf: (el: SepiaElement) => string }, pageList?: PageElement[]): Promise<string | null> {
  // a notebook shows its first page lying on the desk
  const pages = pageList ?? els.filter((e): e is PageElement => e.type === "page");
  if (pages.length) {
    const first = [...pages].sort((a, b) => a.y - b.y)[0];
    const bh = first.h * 1.12;
    const bw = Math.max(first.w * 1.12, (bh * 480) / 320);
    const frame = { x: first.x + first.w / 2 - bw / 2, y: first.y + first.h / 2 - bh / 2, w: bw, h: (bw * 320) / 480 };
    frame.y = first.y - first.h * 0.06;
    const canvas = await renderToCanvas(els, { bg, box: frame, padding: 0, scale: 480 / frame.w, resolveImage, stickerUrl, grid: true, maxPixels: 480 * 320 * 1.2, pages, ...layers });
    const url = canvas.toDataURL("image/jpeg", 0.72);
    releaseCanvas(canvas);
    return url;
  }
  if (!els.length) return null;
  const box = contentBox(els);
  if (!box) return null;
  const W = 480;
  const H = 320;
  // frame the content in a 3:2 window
  const aspect = W / H;
  let bw = box.w + 80;
  let bh = box.h + 80;
  if (bw / bh > aspect) bh = bw / aspect;
  else bw = bh * aspect;
  const frame = { x: box.x + box.w / 2 - bw / 2, y: box.y + box.h / 2 - bh / 2, w: bw, h: bh };
  const canvas = await renderToCanvas(els, { bg, box: frame, padding: 0, scale: W / bw, resolveImage, stickerUrl, grid: true, maxPixels: W * H * 1.2, ...layers });
  const url = canvas.toDataURL("image/jpeg", 0.72);
  releaseCanvas(canvas);
  return url;
}

// ---- SVG ----

const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]!);

async function toDataUrl(url: string): Promise<string> {
  try {
    const blob = await (await fetch(url)).blob();
    return await new Promise((res) => {
      const r = new FileReader();
      r.onload = () => res(String(r.result));
      r.readAsDataURL(blob);
    });
  } catch {
    return url;
  }
}

export async function renderToSvg(els: SepiaElement[], o: RenderOptions): Promise<string> {
  const pad = o.padding ?? 40;
  const content = o.box ?? contentBox(els) ?? { x: 0, y: 0, w: 800, h: 600 };
  const box = { x: content.x - pad, y: content.y - pad, w: content.w + pad * 2, h: content.h + pad * 2 };
  const measure = document.createElement("canvas").getContext("2d")!;
  const parts: string[] = [];
  const defs: string[] = [];
  const paper = PAPERS[o.bg.paper] ?? PAPERS.cream;
  const pages = o.pages?.length ? o.pages : null;
  const rect = (b: Box, fill: string) => `<rect x="${b.x}" y="${b.y}" width="${b.w}" height="${b.h}" fill="${fill}"/>`;
  if (!o.transparent && pages) {
    parts.push(rect(box, deskColor(o.bg)));
    pages.forEach((pg, i) => {
      if (!boxesIntersect(pg, box)) return;
      parts.push(rect(pg, paper.color));
      if (o.grid === false) return;
      const r = pageRuling(pg, pg.pattern ?? o.bg.pattern, o.bg);
      const byColor = new Map<string, string[]>();
      for (const [ax, ay, bx, by, color] of r.lines) {
        if (!byColor.has(color)) byColor.set(color, []);
        byColor.get(color)!.push(`M${ax.toFixed(1)} ${ay.toFixed(1)}L${bx.toFixed(1)} ${by.toFixed(1)}`);
      }
      const dotsByColor = new Map<string, string[]>();
      for (const [x, y, rad, color] of r.dots) {
        if (!dotsByColor.has(color)) dotsByColor.set(color, []);
        dotsByColor.get(color)!.push(`M${(x - rad).toFixed(1)} ${y.toFixed(1)}a${rad} ${rad} 0 1 0 ${rad * 2} 0a${rad} ${rad} 0 1 0 ${-rad * 2} 0`);
      }
      defs.push(`<clipPath id="sepia-page-${i}">${rect(pg, "#000")}</clipPath>`);
      parts.push(
        `<g clip-path="url(#sepia-page-${i})">${[...byColor].map(([c, d]) => `<path d="${d.join("")}" stroke="${c}" stroke-width="1" fill="none"/>`).join("")}${[...dotsByColor].map(([c, d]) => `<path d="${d.join("")}" fill="${c}"/>`).join("")}</g>`
      );
    });
  } else if (!o.transparent) {
    parts.push(rect(box, paper.color));
    if (o.grid !== false && o.bg.pattern !== "blank") {
      // the board's grid, tiled from the world origin like on screen
      const c = o.bg.size;
      const tile = (id: string, w: number, h: number, body: string, x = 0, y = 0) => defs.push(`<pattern id="${id}" x="${x}" y="${y}" width="${w}" height="${h}" patternUnits="userSpaceOnUse">${body}</pattern>`);
      if (o.bg.pattern === "grid") {
        tile("sepia-minor", c, c, `<path d="M0 0H${c}M0 0V${c}" stroke="${paper.line}" stroke-width="1" fill="none"/>`);
        tile("sepia-major", c * 5, c * 5, `<path d="M0 0H${c * 5}M0 0V${c * 5}" stroke="${paper.major}" stroke-width="1" fill="none"/>`);
        parts.push(rect(box, "url(#sepia-minor)"), rect(box, "url(#sepia-major)"));
      } else if (o.bg.pattern === "dots") {
        tile("sepia-minor", c, c, `<circle cx="${c / 2}" cy="${c / 2}" r="1.3" fill="${paper.line}"/>`, -c / 2, -c / 2);
        tile("sepia-major", c * 5, c * 5, `<circle cx="${c * 2.5}" cy="${c * 2.5}" r="1.6" fill="${paper.major}"/>`, -c * 2.5, -c * 2.5);
        parts.push(rect(box, "url(#sepia-minor)"), rect(box, "url(#sepia-major)"));
      } else if (o.bg.pattern === "lines") {
        tile("sepia-rules", c, c * 1.25, `<path d="M0 0H${c}" stroke="${paper.major}" stroke-width="1" fill="none"/>`);
        parts.push(rect(box, "url(#sepia-rules)"));
      } else {
        const w = c * Math.sqrt(3);
        tile("sepia-iso", w, c * 2, `<path d="M0,0L${w},${c * 2}M0,${c * 2}L${w},0M0,${c}H${w}" stroke="${paper.major}" stroke-width="1" fill="none"/>`);
        parts.push(rect(box, "url(#sepia-iso)"));
      }
    }
  }
  const contentStart = parts.length;
  const textLines = (text: string, f: FontKey, size: number, x: number, y: number, w: number, color: string, anchor: string) => {
    measure.font = font(f, size);
    const lh = size * FONTS[f].scale * FONTS[f].lineHeight;
    const lines = wrapLines(measure, text, w);
    return `<text font-family="${esc(FONTS[f].family)}" font-size="${size * FONTS[f].scale}" fill="${color}" text-anchor="${anchor}">${lines
      .map((l, i) => `<tspan x="${x}" y="${y + lh * (i + 0.8)}">${esc(l)}</tspan>`)
      .join("")}</text>`;
  };
  for (const g of layerGroups(els, o)) {
    const start = parts.length;
    for (const el of g.els) {
      const tr = el.type === "line" ? "" : ` transform="translate(${el.x} ${el.y})${el.rot ? ` rotate(${(el.rot * 180) / Math.PI} ${el.w / 2} ${el.h / 2})` : ""}"`;
      const op = el.opacity !== undefined && el.opacity < 1 ? ` opacity="${el.opacity}"` : "";
      switch (el.type) {
        case "ink":
          parts.push(`<path${tr}${op} d="${inkElementPath(el)}" fill="${el.color}" fill-opacity="${PENS[el.pen].opacity}"/>`);
          break;
        case "shape": {
          const ps = sketchShape(el)
            .map((p) => `<path d="${p.d}" stroke="${p.stroke}" stroke-width="${p.strokeWidth}" fill="${p.fill}"${p.dash ? ` stroke-dasharray="${p.dash}"` : ""} stroke-linecap="round" stroke-linejoin="round"/>`)
            .join("");
          const label = el.text ? textLines(el.text, el.font, el.fontSize, el.w / 2, el.h * 0.35, el.w * 0.8, el.textColor, "middle") : "";
          parts.push(`<g${tr}${op}>${ps}${label}</g>`);
          break;
        }
        case "line":
          parts.push(`<g${op}>${sketchLine(el).map((p) => `<path d="${p.d}" stroke="${p.stroke}" stroke-width="${p.strokeWidth}" fill="${p.fill}"${p.dash ? ` stroke-dasharray="${p.dash}"` : ""} stroke-linecap="round" stroke-linejoin="round"/>`).join("")}</g>`);
          break;
        case "text":
          parts.push(`<g${tr}${op}>${textLines(el.text, el.font, el.fontSize, 0, 0, el.autoW ? 0 : el.w, el.color, "start")}</g>`);
          break;
        case "note":
          parts.push(`<g${tr}${op}><path d="M0 0H${el.w}V${el.h * 0.84}L${el.w * 0.84} ${el.h}H0Z" fill="${el.paper}"/>${el.tape ? `<rect x="${el.w * 0.31}" y="-13" width="${el.w * 0.38}" height="26" fill="${el.tape}" opacity="0.8" transform="rotate(-3 ${el.w / 2} 0)"/>` : ""}${textLines(el.text, el.font, el.fontSize, el.w * 0.1, el.h * 0.1, el.w * 0.8, el.textColor, "start")}</g>`);
          break;
        case "card":
          parts.push(`<g${tr}${op}><rect width="${el.w}" height="${el.h}" fill="${el.paper}"/><line x1="0" y1="47" x2="${el.w}" y2="47" stroke="${el.accent}" stroke-opacity="0.55" stroke-width="2"/>${textLines(markdownToLines(el.md).map((l) => l.text).join("\n") || cardTitle(el.md), "sans", el.fontSize, 18, 10, el.w - 36, "#2d241c", "start")}</g>`);
          break;
        case "image": {
          const href = await toDataUrl(o.resolveImage(el.src));
          parts.push(`<g${tr}${op}><image href="${esc(href)}" width="${el.w}" height="${el.h}" preserveAspectRatio="xMidYMid slice"/></g>`);
          break;
        }
        case "frame":
          parts.push(`<g${tr}${op}><rect width="${el.w}" height="${el.h}" fill="none" stroke="${el.color}" stroke-width="2" stroke-dasharray="8 6"/>${textLines(el.title, "hand", 24, 10, -34, 0, "#2b211a", "start")}</g>`);
          break;
        case "sticker": {
          const href = await toDataUrl(o.stickerUrl(el.key));
          parts.push(`<g${tr}${op}><image href="${esc(href)}" width="${el.w}" height="${el.h}"/></g>`);
          break;
        }
      }
    }
    // a layer with its own opacity / blend mode becomes one group
    const opacity = g.layer?.opacity ?? 1;
    const blend = g.layer?.blend ?? "normal";
    if (opacity !== 1 || blend !== "normal") {
      const inner = parts.splice(start);
      parts.push(`<g${opacity !== 1 ? ` opacity="${opacity}"` : ""}${blend !== "normal" ? ` style="mix-blend-mode:${blend}"` : ""}>${inner.join("")}</g>`);
    }
  }
  if (pages) {
    // nothing shows past the edge of a page
    defs.push(`<clipPath id="sepia-pages">${pages.map((pg) => rect(pg, "#000")).join("")}</clipPath>`);
    const inner = parts.splice(contentStart);
    parts.push(`<g clip-path="url(#sepia-pages)">${inner.join("")}</g>`);
  }
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${box.x} ${box.y} ${box.w} ${box.h}" width="${Math.round(box.w)}" height="${Math.round(box.h)}">${defs.length ? `<defs>${defs.join("")}</defs>` : ""}${parts.join("")}</svg>`;
}
